import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import adminService, { AdminNotification } from '../services/adminService';
import { onSnapshot, collection, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, auth, messaging } from '../config/firebase';
import { getToken, onMessage } from 'firebase/messaging';

interface NotificationContextType {
  notifications: AdminNotification[];
  unreadCount: number;
  loading: boolean;
  fcmToken: string | null;
  fcmPermission: NotificationPermission | null;
  requestNotificationPermission: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  createNotification: (notification: Omit<AdminNotification, 'id' | 'createdAt' | 'isRead'>) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Cloud Functions URL - Update this after deploying functions
const FUNCTIONS_URL = 'https://us-central1-pickupninjaapp.cloudfunctions.net';

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [fcmPermission, setFcmPermission] = useState<NotificationPermission | null>(null);

  // ============================================================================
  // FCM PERMISSION & TOKEN REGISTRATION
  // ============================================================================

  const requestNotificationPermission = async () => {
    try {
      console.log('🔔 Requesting notification permission...');

      if (!('Notification' in window)) {
        console.warn('⚠️ This browser does not support notifications');
        return;
      }

      if (!messaging) {
        console.warn('⚠️ Firebase Messaging not initialized');
        return;
      }

      const permission = await Notification.requestPermission();
      setFcmPermission(permission);

      if (permission === 'granted') {
        console.log('✅ Notification permission granted');

        // Get FCM token
        const currentToken = await getToken(messaging, {
          vapidKey: 'BLhLAbLNmNN8WYl4zvTtp_mdy98AefdXtlXaF-Nz83n9bhaF5h74FM-q8pjBJQ6uy-GEblZVDtqqn8W1N8fM6ag'
        });

        if (currentToken) {
          console.log('✅ FCM Token obtained:', currentToken);
          setFcmToken(currentToken);

          // Register token with backend
          await registerTokenWithBackend(currentToken);
        } else {
          console.warn('⚠️ No FCM token available. Request permission to generate one.');
        }
      } else {
        console.log('❌ Notification permission denied');
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
    }
  };

  // Register FCM token with Cloud Functions backend.
  //
  // The endpoint is admin-authenticated (issue #333): we send the signed-in
  // admin's Firebase ID token and the server derives the document id from it.
  // Previously this posted a fabricated `admin-<timestamp>` id with no auth,
  // which created a new token document on every page load.
  const registerTokenWithBackend = async (token: string) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('⚠️ Cannot register FCM token: no signed-in admin');
        return;
      }

      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${FUNCTIONS_URL}/registerToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          token,
          deviceInfo: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        console.log('✅ FCM token registered with backend');
      } else {
        const error = await response.json();
        console.error('❌ Failed to register token with backend:', error);
      }
    } catch (error) {
      console.error('❌ Error registering token with backend:', error);
    }
  };

  // Initialize FCM on component mount
  useEffect(() => {
    if (!messaging) return;

    // Check if permission was previously granted
    if (Notification.permission === 'granted') {
      requestNotificationPermission();
    }

    setFcmPermission(Notification.permission);
  }, []);

  // ============================================================================
  // FOREGROUND MESSAGE HANDLER
  // ============================================================================

  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('📬 Foreground message received:', payload);

      // Show browser notification
      const notificationTitle = payload.notification?.title || 'PickupNinja Admin';
      const notificationOptions = {
        body: payload.notification?.body || 'New notification',
        icon: payload.notification?.icon || '/logo192.png',
        badge: '/logo192.png',
        tag: payload.data?.notificationId || 'default',
        requireInteraction: payload.data?.priority === 'critical' || payload.data?.priority === 'high',
        data: payload.data
      };

      if (Notification.permission === 'granted') {
        new Notification(notificationTitle, notificationOptions);
      }

      // The real-time Firestore listener will automatically update the notifications list
    });

    return () => unsubscribe();
  }, []);

  // ============================================================================
  // REAL-TIME FIRESTORE LISTENER  (admin-gated)
  // ============================================================================

  useEffect(() => {
    let notifUnsub: (() => void) | null = null;

    // Only attach the Firestore listener once a verified admin is signed in.
    const authUnsub = auth.onAuthStateChanged(async (firebaseUser) => {
      // Tear down any previous listener when auth state changes.
      if (notifUnsub) {
        notifUnsub();
        notifUnsub = null;
      }

      if (!firebaseUser) {
        setLoading(false);
        return;
      }

      // Confirm the user is in admin_users before subscribing.
      try {
        const adminSnap = await getDoc(doc(db, 'admin_users', firebaseUser.uid));
        if (!adminSnap.exists()) {
          setLoading(false);
          return;
        }
      } catch {
        setLoading(false);
        return;
      }

      const q = query(
        collection(db, 'admin_notifications'),
        orderBy('createdAt', 'desc')
      );

      notifUnsub = onSnapshot(
        q,
        (snapshot) => {
          const notificationData = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || new Date(d.data().createdAt),
            readAt: d.data().readAt?.toDate?.() || d.data().readAt,
          })) as AdminNotification[];

          setNotifications(notificationData);
          setLoading(false);
        },
        (error) => {
          if (error?.code === 'permission-denied') {
            console.warn('[NotificationContext] Permission denied on admin_notifications — not an admin.');
          } else {
            console.error('Error listening to notifications:', error);
          }
          setLoading(false);
        }
      );
    });

    return () => {
      authUnsub();
      if (notifUnsub) notifUnsub();
    };
  }, []);

  const unreadCount = notifications.filter(notification => !notification.isRead).length;

  const markAsRead = async (notificationId: string) => {
    try {
      await adminService.markNotificationAsRead(notificationId);
      // The real-time listener will update the state
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await adminService.deleteNotification(notificationId);
      // The real-time listener will update the state
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const createNotification = async (notification: Omit<AdminNotification, 'id' | 'createdAt' | 'isRead'>) => {
    try {
      await adminService.createAdminNotification(notification);
      // The real-time listener will update the state
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const refreshNotifications = async () => {
    try {
      setLoading(true);
      const notificationData = await adminService.getAdminNotifications();
      setNotifications(notificationData);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    fcmToken,
    fcmPermission,
    requestNotificationPermission,
    markAsRead,
    deleteNotification,
    createNotification,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
